import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { hostedApiForBrowser, isLocalApiUrl } from '@/utils/hostedApi.mjs';

const envApiUrl = (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL)
    || (typeof process !== 'undefined' && process.env?.VUE_APP_API_URL)
    || '';
const isProd = typeof process !== 'undefined' && process.env?.NODE_ENV === 'production';
const isDevServer = typeof process !== 'undefined' && process.env?.NODE_ENV === 'development';
const browserHost = typeof window !== 'undefined' ? window.location.hostname : '';
const hostedApi = hostedApiForBrowser(browserHost);
const isLocalBrowser = !browserHost
    || browserHost === 'localhost'
    || browserHost === '127.0.0.1'
    || browserHost === 'symphony.test';

/** Resolve API target: hosted domains always use their API, never symphony.test. */
let apiTarget = hostedApi;
if (!apiTarget) {
    if (envApiUrl && !isLocalApiUrl(envApiUrl)) {
        apiTarget = envApiUrl;
    } else if (isDevServer || isLocalBrowser) {
        apiTarget = envApiUrl || 'https://symphony.test';
    } else if (isProd) {
        apiTarget = '';
    } else {
        apiTarget = 'https://symphony.test';
    }
}

// vue-cli dev server proxies /api and /sanctum to apiTarget (same-origin cookies for login).
const baseURL = isDevServer ? '' : apiTarget;
/** Resolved API base for display (where the browser sends requests). */
export const apiBaseDisplay = apiTarget;
export const apiClient = axios.create({
    baseURL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
    },
});

const GUEST_AUTH_PATH_RE =
    /\/api\/v1\/auth\/(login|register|register-company|forgot-password|reset-password|resend-verification)(?:\?|$)/;
const PUBLIC_INVITE_PATH_RE = /\/api\/v1\/user-invites\/(preview|accept)(?:\?|$)/;

function isGuestAuthUrl(url = '') {
    return (
        GUEST_AUTH_PATH_RE.test(url) ||
        PUBLIC_INVITE_PATH_RE.test(url) ||
        url.includes('/sanctum/csrf-cookie')
    );
}

/** Map v1 `{ error: { message, details } }` onto legacy `message` / `errors` for UI catch blocks. */
function attachLegacyErrorFields(error) {
    const body = error?.response?.data;
    if (!body || typeof body !== 'object') {
        return;
    }
    const err = body.error;
    if (!err || typeof err !== 'object') {
        return;
    }
    if (body.message == null && err.message) {
        body.message = err.message;
    }
    if (body.errors == null && err.details?.fields) {
        body.errors = err.details.fields;
    }
}

const getCsrfToken = () => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
        const [name, value] = cookie.trim().split('=');
        if (name === 'XSRF-TOKEN') {
            return decodeURIComponent(value);
        }
    }
    return null;
};

apiClient.interceptors.request.use(
    (config) => {
        const authStore = useAuthStore();

        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }

        if (!String(config.url || '').includes('/sanctum/csrf-cookie')) {
            const csrfToken = getCsrfToken();
            if (csrfToken) {
                config.headers['X-XSRF-TOKEN'] = csrfToken;
            }
        }

        if (authStore.authToken && !isGuestAuthUrl(config.url || '')) {
            config.headers.Authorization = `Bearer ${authStore.authToken}`;
        }
        return config;
    },
    (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
        attachLegacyErrorFields(error);

        if (error.response?.status === 401 || error.response?.status === 403) {
            const authStore = useAuthStore();
            const currentPath = window.location.pathname;
            const isLoginPage =
                currentPath === '/' ||
                currentPath === '/login' ||
                currentPath === '/register' ||
                currentPath === '/accept-invite' ||
                currentPath === '/forgot-password' ||
                currentPath === '/reset-password';

            if (!authStore.authToken && localStorage.getItem('auth_token')) {
                authStore.initializeAuth();
                return Promise.reject(error);
            }

            if (!localStorage.getItem('auth_token') && !isLoginPage) {
                authStore.clearAuth();
                window.location.href = '/';
            }
        }
        return Promise.reject(error);
    }
);

export const handleError = (error) => {
    throw error;
};
